import { ref, toRaw } from 'vue';
import { isObject } from '/@/utils/is';
import filterStore from '../store';
import { BusinessType, FilterLabels, HtmlType, InitialParams, SubmitParams, filterGenerateParams, getDefaultValue, hasLabelAndValueComps, valCanBeNullRules, whetherSubmitLabel } from '../config';
import { cloneDeep } from 'lodash-es';
import { getDictItemsByCode } from '/@/utils/dict';

export default function useInitFilter(initialParams: InitialParams) {
  const { filterType, customParams, initialMaxLength, encodeParams, needLabel, businessType = BusinessType.fusionFilter, searchFn, isShowExtraVacuousList, excludeFields = [] } = initialParams;
  let initialValue = initialParams.initialValue;
  let allSource = [];
  let hasDefaultValue = false;
  const selfSource = ref([]);
  const hasSearchedList = ref([]);
  const filterValue = ref<SubmitParams[]>([]);
  const hasLabelComps = hasLabelAndValueComps(needLabel);

  function initSource() {
    const currentSource = allSource.find((item) => item.filterType === filterType);
    let list = cloneDeep(currentSource?.sysFilterColumnList || []);
    // Only strip defaults from this instance's copy, including defaults applied
    // when a date field is selected or reset. Keep the shared config intact.
    if (initialParams.disableDefaultValues) {
      list.forEach((item) => {
        if (item.otherCustom) delete item.otherCustom.defaultValue;
      });
    }
    if (excludeFields.length) {
      list = list.filter((item) => !excludeFields.includes(item.javaField));
    }
    selfSource.value = list;
    // Restrict options only on this instance's cloned configuration, never the shared dictionary.
    list.forEach((item) => {
      const excludedLabels = initialParams.excludedOptionLabels?.[item.javaField];
      if (!excludedLabels?.length) return;
      const options = item.dictTypeFlag === 'custom' ? item.dictContent : item.dictType ? getDictItemsByCode(item.dictType, item.dictTypeFlag !== 'private') : undefined;
      if (!Array.isArray(options)) return;
      item.dictContent = options.filter((option) => !excludedLabels.includes(option.label));
      item.dictTypeFlag = 'custom';
    });
    if (isObject(customParams) && Object.keys(customParams).length > 0) {
      selfSource.value = selfSource.value.map((item) => {
        if (customParams[item.javaField]) {
          if (isObject(item.requestParameterMapping)) {
            item.requestParameterMapping = {
              ...item.requestParameterMapping,
              ...customParams[item.javaField],
            };
          } else {
            item.requestParameterMapping = {
              ...customParams[item.javaField],
            };
          }
        }
        return item;
      });
    }
    filterInitialValue();
    initFilterValueAndConfig();
  }

  async function init() {
    await querySource();
    // 因为有默认值，需要构造执行搜索的一系列操作
    if (hasDefaultValue && searchFn) {
      searchFn();
    }
  }

  function initFilterValue() {
    const initialMaxShowLength = Math.min(initialMaxLength, selfSource.value.length);
    if (Array.isArray(initialValue) && initialValue.length) {
      // 后端保存的数据不能有id，回显时自己添加
      initialValue.forEach((item, index) => {
        if (!Reflect.has(item, 'id')) {
          item.id = index;
        }
      });
      // 保存一份数据，不干扰初始数据
      const clonedInitialValue = cloneDeep(initialValue);
      // 如果默认值的长度小于初始配置长度，则需要补充默认值，默认值为前n项，且需要过滤掉包含默认值的项
      if (isShowExtraVacuousList && clonedInitialValue.length < initialMaxLength) {
        const neededAddSelfSource = selfSource.value.filter((selfItem) => !clonedInitialValue.some((item) => item.javaField === selfItem.javaField));
        const neededAddInitialValue = setInitialValueFormat(neededAddSelfSource.slice(0, initialMaxShowLength - clonedInitialValue.length));
        filterValue.value = clonedInitialValue.concat(neededAddInitialValue);
      } else {
        filterValue.value = clonedInitialValue;
      }
      return;
    }
    filterValue.value = setInitialValueFormat(selfSource.value.slice(0, initialMaxShowLength));
  }

  function initFilterValueAndConfig() {
    initFilterValue();
    // 获取hasSearchedList
    getSearchedParams();
    setCombineRequestParameterMapping();
  }

  // 初始化默认配置项
  function setInitialValueFormat(source) {
    return source.map((item, index) => {
      const result = {
        field: item.javaField,
        id: index,
      };
      const defaultValue = getDefaultValue(item);
      hasDefaultValue = hasDefaultValue || !!defaultValue;
      if (defaultValue) {
        Object.assign(result, defaultValue);
      }
      return result;
    });
  }

  function reset() {
    initFilterValueAndConfig();
  }

  function getSearchedParams(): filterGenerateParams {
    const selectedLabels: FilterLabels[] = [];
    const functionalResult = filterValue.value.filter((item) => {
      if (!item.field) {
        return false;
      }

      if (valCanBeNullRules.includes(item.rule)) {
        return true;
      }

      if (Array.isArray(item.val)) {
        return item.val.filter((valItem) => isFunctionalValue(valItem)).length;
      }
      return isFunctionalValue(item.val);
    });
    hasSearchedList.value = functionalResult.map((mapItem) => {
      const val = transformResult(mapItem);
      if (needLabel) {
        selectedLabels.push(transformLabels(mapItem, val));
      }
      return {
        dbType: mapItem.javaType,
        type: mapItem.htmlType,
        field: mapItem.field,
        rule: mapItem.rule,
        filterSource: mapItem.filterSource,
        val,
      };
    });
    let submitContentRules;
    if (businessType === BusinessType.projectRule || businessType === BusinessType.taskRule) {
      submitContentRules = [hasSearchedList.value]; // 加一层数组
    } else {
      submitContentRules = hasSearchedList.value;
    }
    const superQueryParams = encodeParams ? encodeURI(JSON.stringify(submitContentRules)) : hasSearchedList.value;
    const params = {
      superQueryMatchType: 'and',
      superQueryParams,
    };

    return {
      ruleContentDTO: params,
      restoreNeedParams: toRaw(functionalResult), // 过滤器回显需要数据
      selectedLabels,
    };
  }

  function transformResult(mapItem) {
    if (valCanBeNullRules.includes(mapItem.rule)) {
      return '';
    }
    if (hasLabelComps.includes(mapItem.htmlType)) {
      if (whetherSubmitLabel(mapItem.returnParameterMapping)) {
        // 签名组件后端保存的label，所以需要下发label
        return mapItem.val?.map((item) => item.label).join(',');
      }
      return mapItem.val?.map((item) => item.value).join(',');
    }
    if ([HtmlType.amount].includes(mapItem.htmlType) && Array.isArray(mapItem.val)) {
      return mapItem.val
        .filter((item) => isFunctionalValue(item))
        .slice()
        .sort((a, b) => a - b)
        .join(',');
    }
    return Array.isArray(mapItem.val) ? mapItem.val?.join(',') : mapItem.val;
  }

  // 默认传当前value值，某些组件有label需要传label
  function transformLabels(item): FilterLabels {
    let valueLabel = item.val;
    if (hasLabelComps.includes(item.htmlType)) {
      valueLabel = item.val?.map((item) => item.label);
    }
    return {
      ...item.filterLabels,
      valueLabel,
      htmlType: item.htmlType, // 对特殊类型数据需要做特别处理
    };
  }

  async function querySource(force = false) {
    const res = await filterStore.getFilterConfigs(force);
    allSource = res;

    initSource();
  }

  async function refreshSource() {
    const currentFilterValue = cloneDeep(toRaw(filterValue.value) || []);
    await querySource(true);
    if (currentFilterValue.length) {
      filterValue.value = currentFilterValue.filter((item) => {
        return !item.field || toRaw(selfSource.value).some((sourceItem) => sourceItem.javaField === item.field);
      });
      setCombineRequestParameterMapping();
      getSearchedParams();
    }
  }

  function isFunctionalValue(val) {
    return val || val === 0;
  }
  // 过滤initialValue中不存在于selfSource的数据
  function filterInitialValue() {
    if (initialValue.length > 0 && selfSource.value.length > 0) {
      initialValue = initialValue.filter((item) => toRaw(selfSource.value).some((sourceItem) => sourceItem.javaField === item.javaField));
    }
  }

  function setInitialValue(value) {
    initialValue = value;
    filterInitialValue();
    initFilterValueAndConfig();
  }

  // 如果有配置combineRequestParameters，那融合搜索项就会有联动，联动规则：根据combineRequestParameters与其他选中项的field匹配，
  // 则会在该接口中添加requestParamterMapping为key为field，value为val的属性，不考虑同一种配置添加多种的情况，
  // 同时也不考虑外层传递的customParam与combineRequestParameters重复的情况
  function setCombineRequestParameterMapping() {
    selfSource.value.forEach((currentSource) => {
      const combineRequestParameters = currentSource.otherCustom?.combineRequestParameters;
      if (combineRequestParameters?.length) {
        const newRequestParameterMapping = Object.assign({}, currentSource.requestParameterMapping);
        combineRequestParameters.forEach((item) => {
          // 先置空，应对删除
          delete newRequestParameterMapping[item];
        });

        const combinedFilterValue = filterValue.value.filter((item) => combineRequestParameters.includes(item.field));
        if (combinedFilterValue.length) {
          combinedFilterValue.forEach((item) => {
            if (!item.val?.length) {
              return;
            }
            newRequestParameterMapping[item.field] = transformResult(item);
          });
        }

        // 只有当requestParameterMapping改变时才需要更新，在子组件中监听该变量做相应操作
        if (JSON.stringify(newRequestParameterMapping) !== JSON.stringify(currentSource.requestParameterMapping)) {
          currentSource.requestParameterMapping = newRequestParameterMapping;
        }
      }
    });
  }

  init();

  return {
    selfSource,
    reset,
    filterValue,
    getSearchedParams,
    hasSearchedList,
    setInitialValue,
    setCombineRequestParameterMapping,
    refreshSource,
  };
}
